import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Phone, MessageCircle, Mail, FileText, X, Sparkles, CheckCircle2,
  AlertTriangle, Clock, Plus, Send, Download, ListChecks, Loader2, Wrench, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/apiClient";
import AppLayout from "@/components/AppLayout";
import ScoreRing, { scoreColor } from "@/components/ScoreRing";
import StarRating from "@/components/StarRating";

const STATUSES = ["new", "reviewing", "contacted", "interviewing", "offer", "hired", "rejected"];
const STATUS_STYLE = {
  new: "bg-[#007AFF]/10 text-[#007AFF]",
  reviewing: "bg-[#5856D6]/10 text-[#5856D6]",
  contacted: "bg-[#FF9500]/10 text-[#FF9500]",
  interviewing: "bg-[#AF52DE]/10 text-[#AF52DE]",
  offer: "bg-[#34C759]/10 text-[#34C759]",
  hired: "bg-[#34C759]/15 text-[#248A3D]",
  rejected: "bg-[#FF3B30]/10 text-[#FF3B30]",
};

export default function JobApplications() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [apps, setApps] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showKit, setShowKit] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [j, a, m] = await Promise.all([
        api.get(`/jobs/${jobId}`),
        api.get(`/jobs/${jobId}/applications`),
        api.get(`/rating-metrics`),
      ]);
      setJob(j.data); setApps(a.data); setMetrics(m.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") { setShowKit(false); setSelected(null); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  // Poll while interview questions are still generating
  useEffect(() => {
    if (!job || job.iq_status !== "generating") return;
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/jobs/${jobId}`); setJob(data); } catch {}
    }, 4000);
    return () => clearTimeout(t);
  }, [job, jobId]);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/jobs/${jobId}/applications/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(job?.title || "applications").replace(/[^a-z0-9-_ ]/gi, "_")}-applications.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const regenQuestions = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/generate-questions`);
      setJob((j) => ({ ...j, interview_questions: data.questions, iq_status: "done" }));
      toast.success("Interview questions ready");
    } catch { toast.error("Failed to generate questions"); }
    finally { setRegenerating(false); }
  };

  // Poll while any application is still being scored
  useEffect(() => {
    const pending = apps.some((a) => a.ai_status === "scoring");
    if (!pending) return;
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/jobs/${jobId}/applications`); setApps(data); } catch {}
    }, 4000);
    return () => clearTimeout(t);
  }, [apps, jobId]);

  const refreshOne = async (id) => {
    const { data } = await api.get(`/applications/${id}`);
    setSelected(data);
    setApps((prev) => prev.map((a) => (a.id === id ? { ...a, ...data } : a)));
  };

  return (
    <AppLayout>
      <button onClick={() => navigate("/jobs")} data-testid="back-to-jobs"
        className="flex items-center gap-1.5 text-sm font-semibold text-[#86868B] hover:text-[#1D1D1F] transition-colors mb-5">
        <ArrowLeft size={16} /> All jobs
      </button>

      {loading ? (
        <div className="text-[#86868B]">Loading…</div>
      ) : (
        <>
          <div className="mb-7 flex items-end justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tighter">{job?.title}</h1>
              <p className="text-[#86868B] mt-1">{apps.length} application{apps.length !== 1 ? "s" : ""} · ranked by AI match score</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowKit(true)} data-testid="interview-kit-btn"
                className="bg-white border border-black/10 text-[#1D1D1F] rounded-full px-4 py-2.5 font-semibold text-sm hover:bg-gray-50 active:scale-95 transition-all flex items-center gap-2">
                <ListChecks size={16} /> Interview Kit
              </button>
              <button onClick={exportCsv} data-testid="export-csv-btn"
                className="bg-white border border-black/10 text-[#1D1D1F] rounded-full px-4 py-2.5 font-semibold text-sm hover:bg-gray-50 active:scale-95 transition-all flex items-center gap-2">
                <Download size={16} /> Export CSV
              </button>
            </div>
          </div>

          {apps.length === 0 ? (
            <div className="bg-white rounded-2xl border border-black/5 p-12 text-center">
              <FileText size={40} className="mx-auto text-[#C7C7CC] mb-3" strokeWidth={1.5} />
              <p className="font-semibold">No applications yet</p>
              <p className="text-sm text-[#86868B]">Share the apply link to start receiving candidates.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {apps.map((a, i) => (
                <motion.div key={a.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                  data-testid="application-row" onClick={() => setSelected(a)}
                  className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/5 p-4 hover:shadow-[0_16px_36px_rgb(0,0,0,0.07)] hover:-translate-y-0.5 transition-all duration-300 cursor-pointer flex items-center gap-4">
                  <ScoreRing score={a.ai_score} pending={a.ai_status === "scoring"} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold tracking-tight truncate">{a.applicant_name}</h3>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full capitalize ${STATUS_STYLE[a.status] || "bg-gray-100 text-[#86868B]"}`}>{a.status}</span>
                    </div>
                    <p className="text-sm text-[#86868B] truncate">{a.email}{a.phone ? ` · ${a.phone}` : ""}</p>
                    {a.ai_recommendation && <p className="text-xs font-semibold mt-0.5" style={{ color: scoreColor(a.ai_score).hex }}>{a.ai_recommendation}</p>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Hotkey href={a.phone ? `tel:${a.phone}` : null} label="Call" icon={Phone} />
                    <Hotkey href={a.phone ? `https://wa.me/${a.phone.replace(/[^0-9]/g, "")}` : null} label="WhatsApp" icon={MessageCircle} color="#25D366" external />
                    <Hotkey href={`mailto:${a.email}`} label="Email" icon={Mail} />
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}

      <AnimatePresence>
        {selected && (
          <Drawer
            key={selected.id}
            appn={selected}
            metrics={metrics}
            onClose={() => setSelected(null)}
            onChanged={() => refreshOne(selected.id)}
          />
        )}
      </AnimatePresence>

      {showKit && (
        <InterviewKit job={job} regenerating={regenerating} onRegen={regenQuestions} onClose={() => setShowKit(false)} />
      )}
    </AppLayout>
  );
}

function InterviewKit({ job, regenerating, onRegen, onClose }) {
  const questions = job?.interview_questions || [];
  const generating = job?.iq_status === "generating";
  const catStyle = { technical: "bg-[#007AFF]/10 text-[#007AFF]", behavioral: "bg-[#AF52DE]/10 text-[#AF52DE]" };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 backdrop-blur-sm px-5 py-8 overflow-y-auto" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl shadow-xl border border-black/5 p-7 w-full max-w-lg" data-testid="interview-kit-modal">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-extrabold tracking-tight flex items-center gap-2"><ListChecks size={20} className="text-[#007AFF]" /> Interview Kit</h2>
          <button onClick={onClose} className="text-[#86868B] hover:text-[#1D1D1F]"><X size={20} /></button>
        </div>
        <p className="text-sm text-[#86868B] mb-5">AI-generated questions for <b>{job?.title}</b>, created when this job was posted.</p>

        {generating ? (
          <div className="text-center py-10 text-[#86868B]">
            <Loader2 size={28} className="animate-spin mx-auto mb-3 text-[#007AFF]" />
            <p className="font-semibold">Generating interview questions…</p>
            <p className="text-sm">This takes a few seconds.</p>
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-[#86868B] mb-4">No questions yet.</p>
            <button onClick={onRegen} disabled={regenerating} data-testid="generate-questions-btn"
              className="bg-[#007AFF] text-white rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#0051FF] active:scale-95 transition-all inline-flex items-center gap-2 disabled:opacity-60">
              {regenerating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate questions
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {questions.map((q, i) => (
                <div key={i} className="bg-[#F5F5F7] rounded-xl p-4 flex gap-3" data-testid="interview-question">
                  <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-xs font-bold text-[#86868B] shrink-0 mt-0.5">{i + 1}</div>
                  <div>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full capitalize mb-1.5 ${catStyle[q.category] || "bg-gray-200 text-[#86868B]"}`}>
                      {q.category === "behavioral" ? <MessageSquare size={10} /> : <Wrench size={10} />} {q.category}
                    </span>
                    <p className="text-sm text-[#1D1D1F] leading-relaxed">{q.question}</p>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={onRegen} disabled={regenerating} data-testid="regenerate-questions-btn"
              className="mt-5 w-full bg-white border border-black/10 rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-gray-50 active:scale-95 transition-all inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {regenerating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Regenerate
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}

function Hotkey({ href, label, icon: Icon, color, external }) {
  const disabled = !href;
  return (
    <a href={href || undefined} target={external ? "_blank" : undefined} rel="noreferrer"
      data-testid={`hotkey-${label.toLowerCase()}`} aria-label={label} title={label}
      onClick={(e) => disabled && e.preventDefault()}
      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-90 ${disabled ? "bg-gray-50 text-[#C7C7CC] cursor-not-allowed" : "bg-gray-100 hover:bg-gray-200 text-[#1D1D1F]"}`}
      style={!disabled && color ? { color } : {}}>
      <Icon size={17} strokeWidth={1.8} />
    </a>
  );
}

function Drawer({ appn, metrics, onClose, onChanged }) {
  const token = localStorage.getItem("token");
  const [note, setNote] = useState("");
  const ratings = appn.ratings || {};

  const setStatus = async (status) => {
    try { await api.patch(`/applications/${appn.id}/status`, { status }); toast.success(`Marked ${status}`); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setMetric = async (metric, value) => {
    try { await api.post(`/applications/${appn.id}/rating`, { metric, value }); onChanged(); }
    catch { toast.error("Failed to save rating"); }
  };
  const setOverall = async (value) => {
    try { await api.post(`/applications/${appn.id}/overall-rating`, { value }); onChanged(); }
    catch { toast.error("Failed"); }
  };
  const addLog = async () => {
    if (!note.trim()) return;
    try { await api.post(`/applications/${appn.id}/log`, { note }); setNote(""); onChanged(); toast.success("Note logged"); }
    catch { toast.error("Failed to log note"); }
  };
  const rescore = async () => {
    try { await api.post(`/applications/${appn.id}/rescore`); toast.success("Re-scoring…"); onChanged(); }
    catch { toast.error("Failed to re-score"); }
  };

  const sc = scoreColor(appn.ai_score);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/25 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ x: 480 }} animate={{ x: 0 }} exit={{ x: 480 }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()} data-testid="application-drawer"
        className="w-full max-w-[480px] h-full bg-[#F5F5F7] overflow-y-auto scrollbar-thin">
        {/* Header */}
        <div className="sticky top-0 z-10 backdrop-blur-xl bg-white/80 border-b border-black/5 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <ScoreRing score={appn.ai_score} pending={appn.ai_status === "scoring"} size={44} stroke={4} />
            <div className="min-w-0">
              <h2 className="font-extrabold tracking-tight truncate">{appn.applicant_name}</h2>
              <p className="text-xs text-[#86868B] truncate">{appn.email}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-drawer" className="text-[#86868B] hover:text-[#1D1D1F] shrink-0"><X size={22} /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Hotkeys */}
          <div className="flex items-center gap-2">
            <Hotkey href={appn.phone ? `tel:${appn.phone}` : null} label="Call" icon={Phone} />
            <Hotkey href={appn.phone ? `https://wa.me/${appn.phone.replace(/[^0-9]/g, "")}` : null} label="WhatsApp" icon={MessageCircle} color="#25D366" external />
            <Hotkey href={`mailto:${appn.email}`} label="Email" icon={Mail} />
            <a href={`${API}/applications/${appn.id}/resume?auth=${token}`} target="_blank" rel="noreferrer" data-testid="view-resume"
              className="ml-auto flex items-center gap-2 text-sm font-semibold text-[#007AFF] bg-[#007AFF]/[0.08] hover:bg-[#007AFF]/15 rounded-full px-4 py-2 transition-colors">
              <FileText size={15} /> Resume
            </a>
          </div>

          {/* AI Assessment */}
          <Card>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={17} style={{ color: sc.hex }} />
              <h3 className="font-bold tracking-tight">AI Assessment</h3>
              {appn.ai_status === "scoring" && <span className="text-xs text-[#86868B] animate-pulse ml-auto">Analyzing…</span>}
              {appn.ai_recommendation && <span className="ml-auto text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: sc.bg, color: sc.hex }}>{appn.ai_recommendation}</span>}
            </div>
            {appn.ai_status === "failed" ? (
              <div>
                <p className="text-sm text-[#FF3B30]">Scoring failed. The resume may be unreadable.</p>
                <button onClick={rescore} data-testid="rescore-btn"
                  className="mt-3 text-sm font-semibold text-[#007AFF] bg-[#007AFF]/[0.08] hover:bg-[#007AFF]/15 rounded-full px-4 py-2 transition-colors flex items-center gap-2">
                  <Sparkles size={14} /> Re-run AI scoring
                </button>
              </div>
            ) : appn.ai_status === "scoring" ? (
              <p className="text-sm text-[#86868B]">Our AI is reviewing this resume against the job description. This takes a few seconds.</p>
            ) : (
              <>
                <p className="text-sm text-[#1D1D1F] leading-relaxed">{appn.ai_summary}</p>
                {appn.ai_strengths?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-bold uppercase tracking-wide text-[#34C759] mb-1.5 flex items-center gap-1"><CheckCircle2 size={13} /> Strengths</p>
                    <ul className="space-y-1">{appn.ai_strengths.map((s, i) => <li key={i} className="text-sm text-[#1D1D1F] flex gap-2"><span className="text-[#34C759] mt-1">•</span>{s}</li>)}</ul>
                  </div>
                )}
                {appn.ai_concerns?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-bold uppercase tracking-wide text-[#FF9500] mb-1.5 flex items-center gap-1"><AlertTriangle size={13} /> Concerns</p>
                    <ul className="space-y-1">{appn.ai_concerns.map((s, i) => <li key={i} className="text-sm text-[#1D1D1F] flex gap-2"><span className="text-[#FF9500] mt-1">•</span>{s}</li>)}</ul>
                  </div>
                )}
              </>
            )}
          </Card>

          {/* Status */}
          <Card>
            <h3 className="font-bold tracking-tight mb-3">Status</h3>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => setStatus(s)} data-testid={`status-${s}`}
                  className={`text-xs font-bold px-3 py-1.5 rounded-full capitalize transition-all active:scale-95 ${appn.status === s ? STATUS_STYLE[s] + " ring-2 ring-offset-1 ring-current" : "bg-gray-100 text-[#86868B] hover:bg-gray-200"}`}>
                  {s}
                </button>
              ))}
            </div>
          </Card>

          {/* Enrichment ratings */}
          <Card>
            <h3 className="font-bold tracking-tight mb-1">Recruiter Ratings</h3>
            <p className="text-xs text-[#86868B] mb-4">Tap to rate. Your team's assessment.</p>
            <div className="space-y-3">
              {metrics.map((m) => (
                <div key={m.id} className="flex items-center justify-between">
                  <span className="text-sm font-medium">{m.name}</span>
                  <StarRating value={ratings[m.name] || 0} max={m.scale} onChange={(v) => setMetric(m.name, v)} size={20} testId={`metric-${m.name.toLowerCase().replace(/\s/g, "-")}`} />
                </div>
              ))}
              <div className="flex items-center justify-between pt-3 border-t border-black/5">
                <span className="text-sm font-bold">Overall</span>
                <StarRating value={appn.overall_rating || 0} max={5} onChange={setOverall} size={22} testId="overall-rating" />
              </div>
            </div>
          </Card>

          {/* Application answers */}
          {appn.form_data && Object.keys(appn.form_data).length > 0 && (
            <Card>
              <h3 className="font-bold tracking-tight mb-3">Application Answers</h3>
              <div className="space-y-2.5">
                {Object.entries(appn.form_data).map(([k, v]) => (
                  <div key={k}>
                    <p className="text-xs font-bold text-[#86868B]">{k}</p>
                    <p className="text-sm">{v || "—"}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Conversation log */}
          <Card>
            <h3 className="font-bold tracking-tight mb-3">Conversation Log</h3>
            <div className="space-y-3 mb-3">
              {(appn.logs || []).length === 0 && <p className="text-sm text-[#86868B]">No notes yet.</p>}
              {(appn.logs || []).slice().reverse().map((l) => (
                <div key={l.id} className="bg-[#F5F5F7] rounded-xl p-3">
                  <p className="text-sm">{l.note}</p>
                  <p className="text-[11px] text-[#86868B] mt-1 flex items-center gap-1"><Clock size={11} /> {l.author} · {new Date(l.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addLog()} data-testid="log-input"
                placeholder="Log a call, note, feedback…"
                className="flex-1 bg-gray-50/70 border border-black/10 rounded-full px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 outline-none transition-colors" />
              <button onClick={addLog} data-testid="add-log-btn" className="w-10 h-10 rounded-full bg-[#007AFF] text-white flex items-center justify-center hover:bg-[#0051FF] active:scale-90 transition-all shrink-0"><Send size={16} /></button>
            </div>
          </Card>
        </div>
      </motion.div>
    </div>
  );
}

function Card({ children }) {
  return <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/5 p-5">{children}</div>;
}
